/**
 * message-notify-tab.tsx — Cài đặt → Nhắn tin & tiếp nhận → Thông báo tin nhắn.
 *
 * Mẫu SaleSmartly "Thông báo tin nhắn": sự kiện nào báo cho ai (cấp tổ chức).
 * CÁCH báo (âm thanh, trình duyệt, nháy tab) là tuỳ chọn riêng từng người ở
 * Cài đặt cá nhân → Thông báo. Backend: PUT /notification-settings cần settings.update.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Info, Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import {
  useMessageNotifySettings, useSaveMessageNotifySettings, type MessageNotifySettings,
} from '@/hooks/use-notification-settings'

const MINUTE_PRESETS = [5, 10, 15, 30, 60]
const MINUTES_MAX = 24 * 60

// `label`/`hint` là getter đọc i18n.t() lúc dùng — KHÔNG spread {...option} ở cấp module khi tiêu thụ mảng
// này (đóng băng giá trị lúc nạp module, xem bẫy ở pages/settings/settings-modules.ts).
const ASSIGNED_OPTIONS: { id: MessageNotifySettings['assigned']; label: string; hint: string }[] = [
  {
    id: 'assignee',
    get label() { return i18n.t('settings:messageNotify.assignedOptions.assignee.label') },
    get hint() { return i18n.t('settings:messageNotify.assignedOptions.assignee.hint') },
  },
  {
    id: 'all',
    get label() { return i18n.t('settings:messageNotify.assignedOptions.all.label') },
    get hint() { return i18n.t('settings:messageNotify.assignedOptions.all.hint') },
  },
]

function ToggleRow({
  label, hint, checked, onChange,
}: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start justify-between gap-4 px-4 py-3">
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} className="mt-0.5" />
    </label>
  )
}

export function MessageNotifyTab() {
  const { t } = useTranslation('settings')
  const { data, isLoading, isError, error } = useMessageNotifySettings()
  const save = useSaveMessageNotifySettings()
  const [draft, setDraft] = useState<MessageNotifySettings | null>(null)
  const [customMinutes, setCustomMinutes] = useState('')

  useEffect(() => {
    if (!data) return
    setDraft(data)
    setCustomMinutes(MINUTE_PRESETS.includes(data.reminder.minutes) ? '' : String(data.reminder.minutes))
  }, [data])

  const dirty = useMemo(() => !!data && !!draft && JSON.stringify(data) !== JSON.stringify(draft), [data, draft])

  if (isLoading) return <Loading label={t('settings:messageNotify.loadingLabel')} />
  if (isError || !data || !draft) return <ErrorState message={error ? apiError(error) : undefined} />

  const set = (patch: Partial<MessageNotifySettings>) => setDraft((d) => (d ? { ...d, ...patch } : d))
  const setReminder = (patch: Partial<MessageNotifySettings['reminder']>) =>
    setDraft((d) => (d ? { ...d, reminder: { ...d.reminder, ...patch } } : d))
  const remOff = !draft.reminder.enabled

  async function onSave() {
    try {
      await save.mutateAsync(draft!)
      toast.success(t('settings:messageNotify.savedToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="max-w-4xl space-y-5">
      <div className="flex gap-2.5 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="space-y-1">
          <p><Trans t={t} i18nKey="settings:messageNotify.infoIntro" components={{ b: <b /> }} /></p>
          <p className="text-xs text-muted-foreground">
            <Trans
              t={t}
              i18nKey="settings:messageNotify.hintLink"
              components={{ link: <Link to="/account/notifications" className="text-primary underline-offset-2 hover:underline" /> }}
            />
          </p>
        </div>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-medium">{t('settings:messageNotify.newMessageSection')}</h2>
        <div className="divide-y rounded-lg border bg-card">
          <ToggleRow
            label={t('settings:messageNotify.aiList.label')}
            hint={t('settings:messageNotify.aiList.hint')}
            checked={draft.aiList}
            onChange={(v) => set({ aiList: v })}
          />
          <ToggleRow
            label={t('settings:messageNotify.unassigned.label')}
            hint={t('settings:messageNotify.unassigned.hint')}
            checked={draft.unassigned}
            onChange={(v) => set({ unassigned: v })}
          />
          <div className="space-y-2 px-4 py-3">
            <div>
              <p className="text-sm font-medium">{t('settings:messageNotify.assigned.title')}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{t('settings:messageNotify.assigned.hint')}</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t('settings:messageNotify.assigned.ariaLabel')}>
              {ASSIGNED_OPTIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={draft.assigned === o.id}
                  onClick={() => set({ assigned: o.id })}
                  className={cn(
                    'rounded-lg border px-3 py-2.5 text-left transition-colors',
                    draft.assigned === o.id ? 'border-primary bg-primary/5' : 'hover:bg-accent',
                  )}
                >
                  <span className="block text-sm font-medium">{o.label}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">{o.hint}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium">{t('settings:messageNotify.reminderSection')}</h2>
        <div className="divide-y rounded-lg border bg-card">
          <ToggleRow
            label={t('settings:messageNotify.reminderEnabled.label')}
            hint={t('settings:messageNotify.reminderEnabled.hint')}
            checked={draft.reminder.enabled}
            onChange={(v) => setReminder({ enabled: v })}
          />
          <div className={cn('space-y-2 px-4 py-3', remOff && 'opacity-60')}>
            <p className="text-sm font-medium">{t('settings:messageNotify.reminderAfter')}</p>
            <div className="flex flex-wrap items-center gap-2">
              {MINUTE_PRESETS.map((m) => (
                <button
                  key={m}
                  type="button"
                  disabled={remOff}
                  aria-pressed={draft.reminder.minutes === m && customMinutes === ''}
                  onClick={() => { setCustomMinutes(''); setReminder({ minutes: m }) }}
                  className={cn(
                    'h-8 rounded-md border px-3 text-sm transition-colors',
                    draft.reminder.minutes === m && customMinutes === '' ? 'border-primary bg-primary/5 text-primary' : 'hover:bg-accent',
                  )}
                >
                  {t('settings:messageNotify.minutesOption', { count: m })}
                </button>
              ))}
              <span className="text-sm text-muted-foreground">{t('settings:messageNotify.or')}</span>
              <Input
                type="number" min={1} max={MINUTES_MAX} inputMode="numeric" disabled={remOff}
                placeholder={t('settings:messageNotify.customPlaceholder')}
                value={customMinutes}
                onChange={(e) => {
                  setCustomMinutes(e.target.value)
                  const n = Math.round(Number(e.target.value))
                  if (Number.isFinite(n) && n >= 1) setReminder({ minutes: Math.min(MINUTES_MAX, n) })
                }}
                className="h-8 w-28"
                aria-label={t('settings:messageNotify.customMinutesAriaLabel')}
              />
              <span className="text-sm text-muted-foreground">{t('settings:messageNotify.minutesUnit')}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('settings:messageNotify.reminderFooterHint')}
            </p>
          </div>
        </div>
      </section>

      <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-background/95 py-3 backdrop-blur">
        <Button
          variant="ghost"
          disabled={!dirty || save.isPending}
          onClick={() => {
            setDraft(data)
            setCustomMinutes(MINUTE_PRESETS.includes(data.reminder.minutes) ? '' : String(data.reminder.minutes))
          }}
        >
          {t('settings:messageNotify.discardChanges')}
        </Button>
        <Button onClick={onSave} disabled={!dirty || save.isPending}>
          {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
          {t('common:actions.save')}
        </Button>
      </div>
    </div>
  )
}
